/**
 * expiryDataSource.ts — WatermelonDB → ExpiryInput bridge.
 *
 * Batches belong to products, and products are store-scoped, so the store's
 * batches are found via its products. Only batches with stock on hand and a
 * recorded expiry date are considered. Unit cost falls back from the batch's
 * own `cost_price` to the product's `purchase_price` when the batch has none.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Product, Category, Batch } from '@/db/models/Inventory';
import type { ExpiryInput } from './expiry';

export async function loadExpiry(
  storeId: string,
  nearWindowDays = 30,
  soonWindowDays = 90,
  asOf: Date = new Date(),
): Promise<ExpiryInput> {
  // Store's physical products (batches hang off these).
  const products = await database.get<Product>('products').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('is_service', false),
  ).fetch();

  const productById = new Map(products.map((p) => [p.id, p]));
  const productIds = products.map((p) => p.id);

  // Category names.
  const categoryIds = [
    ...new Set(products.map((p) => p.categoryId).filter((id): id is string => !!id)),
  ];
  const categoryRows = await fetchInChunks(categoryIds, (batch) =>
    database.get<Category>('categories').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const categoryName = new Map(categoryRows.map((c) => [c.id, c.name]));

  // In-stock batches for those products.
  const batchRows = await fetchInChunks(productIds, (ids) =>
    database.get<Batch>('batches').query(
      Q.where('product_id', Q.oneOf(ids)),
      Q.where('is_deleted', false),
      Q.where('quantity', Q.gt(0)),
    ).fetch(),
  );

  const batches = batchRows.flatMap((b) => {
    // Skip batches with no expiry date — nothing to age.
    if (!b.expiryDate) return [];
    const product = productById.get(b.productId);
    const unitCost = b.costPrice ?? product?.purchasePrice ?? 0;
    return [{
      batchNumber: b.batchNumber,
      productId: b.productId,
      productName: product?.name ?? 'Unknown product',
      category: (product?.categoryId && categoryName.get(product.categoryId)) || 'Uncategorised',
      expiryDate: b.expiryDate,
      quantity: b.quantity,
      unitCost,
    }];
  });

  return { batches, asOf, nearWindowDays, soonWindowDays };
}
